/**
 * usePbSubscription — PocketBase real-time subscription hook
 *
 * Subscribes to a collection and invalidates the given TanStack Query key
 * whenever a record is created, updated, or deleted. The query re-fetches
 * automatically, keeping the UI live without polling.
 *
 * Retry logic: if subscription fails, retries with exponential backoff
 * (1s x 2^n) up to MAX_RETRIES attempts. Retry counter resets on each
 * successful message received.
 */

import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import pb from '@/lib/pb'

const MAX_RETRIES = 5
const BASE_DELAY_MS = 1000

export function usePbSubscription(
  collection: string,
  queryKey: unknown[],
  filter?: string
) {
  const queryClient = useQueryClient()
  const queryKeyStr = JSON.stringify(queryKey)
  const retriesRef = useRef(0)

  useEffect(() => {
    const parsedKey = JSON.parse(queryKeyStr) as unknown[]
    let unsubscribe: (() => void) | null = null
    let retryTimeout: ReturnType<typeof setTimeout> | null = null
    let cancelled = false

    const subscribe = async () => {
      if (cancelled) return
      try {
        unsubscribe = await pb.collection(collection).subscribe(
          filter ?? '*',
          () => {
            // Reset retry counter on successful message
            retriesRef.current = 0
            // Any change -> invalidate -> TanStack Query re-fetches
            queryClient.invalidateQueries({ queryKey: parsedKey })
          }
        )
        retriesRef.current = 0
      } catch (err) {
        console.warn(`[usePbSubscription] Failed to subscribe to ${collection}:`, err)
        if (!cancelled && retriesRef.current < MAX_RETRIES) {
          const delay = BASE_DELAY_MS * Math.pow(2, retriesRef.current)
          retriesRef.current++
          retryTimeout = setTimeout(subscribe, delay)
        }
      }
    }

    subscribe()

    return () => {
      cancelled = true
      if (retryTimeout) clearTimeout(retryTimeout)
      if (unsubscribe) unsubscribe()
    }
  }, [collection, queryClient, filter, queryKeyStr])
}
